import {GameStateEnums} from '../../../../../shared/GameEnums'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGameManager} from '../UIGameManager'
import {UIGameActionButtonMultiView} from '../ui_game_actions/UIGameActionButtonMultiView'
import {UIGameActionButtonViewKnightActivation} from '../ui_game_actions/UIGameActionButtonViewKnightActivation'
import {UIGameActionButtonViewKnightPlacement} from '../ui_game_actions/UIGameActionButtonViewKnightPlacement'
import {UIGameActionButtonViewKnightUpgrade} from '../ui_game_actions/UIGameActionButtonViewKnightUpgrade'
import {UIGameActionButtonViewKnightActions} from '../ui_game_actions/UIGameActionButtonViewKnightActions'
import {UIGamePlayerInformationKnightView} from './UIGamePlayerInformationKnightView'
import {PIXI} from '@colonist/pixi'
import {UIKnightPlacementHoverActionController} from '../ui_game_hover_actions/UIKnightPlacementHoverActionController'
import {UIKnightActivationHoverActionController} from '../ui_game_hover_actions/UIKnightActivationHoverActionController'
import {UIKnightUpgradeHoverActionController} from '../ui_game_hover_actions/UIKnightUpgradeHoverActionController'
import {UIKnightActionHoverActionController} from '../ui_game_hover_actions/UIKnightActionHoverActionController'
import {UIGameActionButtonPositions} from '../ui_game_actions/UIGameActionsController'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {MechanicKnightState, MechanicPirateState, MechanicRobberState, PlayerColor} from '@colonist/game-states'
import {MechanicValidatorKnight, MechanicValidatorKnightActions, MechanicValidatorKnightPlayer} from '@colonist/game-mechanic-validators'

export abstract class UIGameKnightController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    private readonly knightValidator: MechanicValidatorKnight
    private readonly knightActionValidator: MechanicValidatorKnightActions
    private readonly knightState: MechanicKnightState
    private readonly robberState: MechanicRobberState | undefined
    private readonly pirateState: MechanicPirateState | undefined

    private playerInformationKnightIconViews: UIGamePlayerInformationKnightView[]

    constructor(uiGameManager: UIGameManager, knightState: MechanicKnightState, robberState: MechanicRobberState | undefined, pirateState: MechanicPirateState | undefined) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.knightValidator = new MechanicValidatorKnight(this.uiGameManager.gameController.getPlayerValidators(), this.uiGameManager.gameController.mapValidator, knightState)
        this.knightActionValidator = new MechanicValidatorKnightActions(this.uiGameManager.gameController.getPlayerValidators(), this.uiGameManager.gameController.mapValidator, knightState, robberState, pirateState)
        this.knightState = knightState
        this.robberState = robberState
        this.pirateState = pirateState

        this.playerInformationKnightIconViews = []
        this.initPlayerInformationKnightIconViews()
        this.initializeHoverActions()
    }

    private initializeHoverActions() {
        if(this.uiGameManager.gameController.isPlayerSpectator) return
        if(this.uiGameManager.gameController.isReplay) return

        const myKnightValidator = this.knightValidator.getKnightPlayerValidator(this.uiGameManager.gameController.myColor)
        new UIKnightPlacementHoverActionController(this.uiGameManager, myKnightValidator)
        new UIKnightUpgradeHoverActionController(this.uiGameManager, myKnightValidator)
        new UIKnightActivationHoverActionController(this.uiGameManager, myKnightValidator)
        new UIKnightActionHoverActionController(this.uiGameManager, this.knightActionValidator)
    }

    private initPlayerInformationKnightIconViews() {
        this.playerInformationKnightIconViews.forEach((view) => view.destroy())
        this.playerInformationKnightIconViews = []

        for(const knightPlayerValidator of this.knightValidator.getKnightPlayerValidators()) {
            const newKnightIconView = this.createPlayerInformationKnightView(knightPlayerValidator)
            this.playerInformationKnightIconViews.push(newKnightIconView)
        }

        this.highlightPlayerWithHighestKnightStrength()
    }

    protected abstract createPlayerInformationKnightView(knightPlayerValidator: MechanicValidatorKnightPlayer): UIGamePlayerInformationKnightView

    private getPlayerInformationKnightIconView(player: PlayerColor): UIGamePlayerInformationKnightView | undefined {
        for(const playerInformationKnightIconView of this.playerInformationKnightIconViews) {
            if(playerInformationKnightIconView.knightPlayerValidator.playerColor === player) return playerInformationKnightIconView
        }
        return undefined
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.KnightStateUpdated: this.highlightPlayerWithHighestKnightStrength(); break
            case UIGameEventEnums.CanvasResized: this.initPlayerInformationKnightIconViews(); break
            default: break
        }
    }

    protected abstract createButtonMultiView(preferredPosition: number, buttonContainer: PIXI.Container): UIGameActionButtonMultiView

    protected initKnightButton() {
        if(this.uiGameManager.gameController.isPlayerSpectator) return

        const buttonContainer = this.uiGameManager.actionsController.container
        const newMultiButton = this.createButtonMultiView(UIGameActionButtonPositions.KnightMultiButton, buttonContainer)
        const knightTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_active_level1)
        newMultiButton.addCenteredIcon(knightTexture)
        newMultiButton.setTip({key: 'strings:game.tooltips.knightActions'}, [{key: 'strings:colonistCommon.utils.emptyString'}])
        this.uiGameManager.actionsController.addButton(newMultiButton)

        const myKnightValidator = this.knightValidator.getKnightPlayerValidator(this.uiGameManager.gameController.myColor)
        newMultiButton.addButton(new UIGameActionButtonViewKnightPlacement(this.uiGameManager, buttonContainer, newMultiButton, myKnightValidator))
        newMultiButton.addButton(new UIGameActionButtonViewKnightActivation(this.uiGameManager, buttonContainer, newMultiButton, myKnightValidator))
        newMultiButton.addButton(new UIGameActionButtonViewKnightUpgrade(this.uiGameManager, buttonContainer, newMultiButton, myKnightValidator))
        newMultiButton.addButton(new UIGameActionButtonViewKnightActions(this.uiGameManager, buttonContainer, newMultiButton, this.knightState, this.robberState, this.pirateState))
    }

    private highlightPlayerWithHighestKnightStrength() {
        const playersWithHighestKnightStrength = this.knightValidator.getPlayersWithHighestKnightStrength()
        const doesEveryPlayerHaveZeroKnightStrength = this.knightValidator.getTotalKnightStrength() === 0
        for(const playerColor of this.uiGameManager.gameController.getPlayerColors()) {
            const playerInformationKnightIconView = this.getPlayerInformationKnightIconView(playerColor)
            if(playerInformationKnightIconView == undefined) continue

            if(doesEveryPlayerHaveZeroKnightStrength) {
                playerInformationKnightIconView.highlight(false)
                continue
            }

            const doesPlayerHaveHighestKnightStrength = playersWithHighestKnightStrength.includes(playerColor)
            playerInformationKnightIconView.highlight(doesPlayerHaveHighestKnightStrength)
        }
    }
}
